import type { ILeadQueueRepository } from '@/domain/repositories/ILeadQueueRepository';
import type { QueuedLead } from '@/domain/entities/QueuedLead';
import { isPermanentSyncFailure } from '@/domain/entities/QueuedLead';
import {
  LEAD_QUEUE_STORE,
  openActivationRuntimeDb,
  promisifyRequest,
} from '@/infrastructure/indexeddb/activationRuntimeDb';

/**
 * Implementação de `ILeadQueueRepository` sobre a object store `leadQueue`
 * do IndexedDB (HUB-236). Cada método abre sua própria conexão e a fecha ao
 * final — simplicidade deliberada (a spec não define meta de performance
 * formal para esta fatia, ver Spec Técnica > Considerações de performance).
 */
export class IndexedDbLeadQueueRepository implements ILeadQueueRepository {
  async add(lead: QueuedLead): Promise<number> {
    const db = await openActivationRuntimeDb();
    try {
      const tx = db.transaction(LEAD_QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(LEAD_QUEUE_STORE);
      // Ambas as requisições são emitidas antes de qualquer `await` para
      // manter a transação viva (issue-then-await é o padrão seguro com
      // IndexedDB + Promises — um `await` entre duas chamadas na mesma
      // transação arrisca ela ser encerrada antes da segunda operação).
      const addRequest = store.add(lead);
      const countRequest = store.count();
      await promisifyRequest(addRequest);
      return await promisifyRequest(countRequest);
    } finally {
      db.close();
    }
  }

  async count(): Promise<number> {
    const db = await openActivationRuntimeDb();
    try {
      const tx = db.transaction(LEAD_QUEUE_STORE, 'readonly');
      return await promisifyRequest(tx.objectStore(LEAD_QUEUE_STORE).count());
    } finally {
      db.close();
    }
  }

  async getPendingForEvent(eventId: string): Promise<QueuedLead[]> {
    const db = await openActivationRuntimeDb();
    try {
      const tx = db.transaction(LEAD_QUEUE_STORE, 'readonly');
      const index = tx.objectStore(LEAD_QUEUE_STORE).index('eventId');
      const leads = await promisifyRequest(index.getAll(IDBKeyRange.only(eventId)));
      // Sem índice composto [eventId, createdAt] (Spec Técnica > Modelo de
      // dados só define os dois índices simples) — ordena em memória; o
      // hard limit de 10.000 registros por fila mantém isso barato.
      return [...leads].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    } finally {
      db.close();
    }
  }

  async remove(id: string): Promise<void> {
    const db = await openActivationRuntimeDb();
    try {
      const tx = db.transaction(LEAD_QUEUE_STORE, 'readwrite');
      await promisifyRequest(tx.objectStore(LEAD_QUEUE_STORE).delete(id));
    } finally {
      db.close();
    }
  }

  async recordRetryFailure(
    id: string,
    lastError: { status: number; message: string },
    lastRetryAt: string
  ): Promise<void> {
    const db = await openActivationRuntimeDb();
    try {
      const tx = db.transaction(LEAD_QUEUE_STORE, 'readwrite');
      const store = tx.objectStore(LEAD_QUEUE_STORE);
      const existing = await promisifyRequest(store.get(id));
      // O lead pode já ter sido removido por uma tentativa concorrente
      // (ex.: Background Sync e polling fallback disparando perto um do
      // outro) — nada a fazer, não é um erro.
      if (!existing) return;

      const updated: QueuedLead = {
        ...existing,
        retryCount: existing.retryCount + 1,
        lastRetryAt,
        lastError,
      };
      await promisifyRequest(store.put(updated));
    } finally {
      db.close();
    }
  }

  async hasPermanentFailure(): Promise<boolean> {
    const db = await openActivationRuntimeDb();
    try {
      const tx = db.transaction(LEAD_QUEUE_STORE, 'readonly');
      const leads = await promisifyRequest<QueuedLead[]>(tx.objectStore(LEAD_QUEUE_STORE).getAll());
      // Sem índice dedicado por status de erro (fora do modelo de dados
      // desta fatia) — varredura em memória, mesma simplicidade deliberada
      // das demais operações deste repositório; o hard limit de 10.000
      // registros (`QUEUE_HARD_LIMIT`) mantém isso barato.
      return leads.some((lead) => lead.lastError !== null && isPermanentSyncFailure(lead.lastError.status));
    } finally {
      db.close();
    }
  }
}
